import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import GlobalFormShell from "../../../../../global/forms/GlobalFormShell";
import {
  GlobalButton,
  GlobalSelectField,
  GlobalTextField,
} from "../../../../../global/forms/GlobalFormFields";
import { VISITOR_MASTER_STATUS_OPTIONS } from "../../../utils/visitorMasterStatusOptions";
import {
  visitorPurposeSchema,
  visitorPurposeFormDefaults,
} from "../../../utils/visitorPurposeValidation";
import { mapApiDetailsToFormErrors } from "../../../utils/visitorHelpers";

const ALERT_ID = "visitor-purpose-form-alert";

export default function PurposeForm({
  mode = "create",
  initialValues,
  onSubmit,
  onCancel,
  submitting = false,
  serverError = "",
}) {
  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(visitorPurposeSchema),
    defaultValues: initialValues || visitorPurposeFormDefaults,
    mode: "onBlur",
  });

  useEffect(() => {
    if (initialValues) reset(initialValues);
  }, [initialValues, reset]);

  const applyServerDetails = (details) => {
    const mapped = mapApiDetailsToFormErrors(details);
    Object.entries(mapped).forEach(([field, message]) => {
      setError(field, { type: "server", message });
    });
  };

  const submitHandler = async (values) => {
    try {
      await onSubmit?.({
        purpose_name: String(values.purpose_name ?? "").trim(),
        status_id: Number(values.status_id),
      });
    } catch (err) {
      if (err?.details) applyServerDetails(err.details);
      throw err;
    }
  };

  return (
    <form onSubmit={handleSubmit(submitHandler)} noValidate>
      <GlobalFormShell
        legend={mode === "edit" ? "Edit visit purpose" : "Add visit purpose"}
        alertId={ALERT_ID}
        showAlert={Boolean(serverError)}
        alertText={serverError}
        gridClassName="gfs-form-grid--compact"
        actions={
          <>
            {onCancel ? (
              <GlobalButton type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
                Cancel
              </GlobalButton>
            ) : null}
            <GlobalButton type="submit" variant="primary" disabled={submitting}>
              {submitting ? "Saving…" : mode === "edit" ? "Save changes" : "Create purpose"}
            </GlobalButton>
          </>
        }
      >
        <Controller
          name="purpose_name"
          control={control}
          render={({ field }) => (
            <GlobalTextField
              id="purpose_name"
              label="Purpose name"
              required
              value={field.value}
              onChange={field.onChange}
              error={errors.purpose_name?.message}
              placeholder="e.g. Meeting, Delivery"
              baseAlertId={ALERT_ID}
            />
          )}
        />
        <Controller
          name="status_id"
          control={control}
          render={({ field }) => (
            <GlobalSelectField
              id="purpose_status_id"
              label="Status"
              required
              value={field.value}
              onChange={field.onChange}
              error={errors.status_id?.message}
              options={VISITOR_MASTER_STATUS_OPTIONS}
              placeholderOption="Select status"
              baseAlertId={ALERT_ID}
            />
          )}
        />
      </GlobalFormShell>
    </form>
  );
}
